import React, { useEffect, useState } from 'react';
import { Link as LinkIcon, AlertTriangle, CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * MagicLinkHealthCard — Sprint 200
 * ────────────────────────────────
 * Command Centre tile showing per-flow funnel metrics for every HiBFF
 * magic-link surface (parent onboarding, charter invite, hearts approval,
 * renewal reminder, anniversary reunion, …).
 *
 * Read-only. Self-fetches `/api/admin/magic-links/health?days=30`.
 *
 * Per-row anomaly chips:
 *   • alert  — non-zero minted but zero sent → email/SMS broken
 *   • warn   — < 15% conversion on a non-trivial sample → product issue
 *   • ok     — healthy
 *   • empty  — no traffic in window (greyed out)
 */

const PURPLE = '#7B4FD8';

const labelFor = (slug) => {
  const map = {
    parent_onboarding: 'Parent onboarding',
    teen_charter_invite: 'Charter invite (teen)',
    subscription_renewal_reminder: 'Renewal reminder',
    anniversary_reunion: 'Anniversary reunion',
    hearts_approval_request: 'Hearts approval',
  };
  return map[slug] || slug.replace(/_/g, ' ');
};

const anomalyChip = (a) => {
  if (a === 'alert') return { color: '#DC2626', bg: '#FEE2E2', icon: <AlertTriangle size={11} />, label: 'Broken?' };
  if (a === 'warn')  return { color: '#B45309', bg: '#FEF3C7', icon: <AlertTriangle size={11} />, label: 'Low' };
  if (a === 'empty') return { color: '#9CA3AF', bg: '#F3F4F6', icon: <Circle size={11} />, label: 'Idle' };
  return { color: '#059669', bg: '#D1FAE5', icon: <CheckCircle2 size={11} />, label: 'OK' };
};

const MagicLinkHealthCard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);

  const load = () => {
    apiClient.request('/api/admin/magic-links/health?days=30')
      .then(setData)
      .catch((err) => setError(err?.message || 'Failed to load'));
  };

  useEffect(() => { load(); }, []);

  const renewalBroken = data?.flows?.some(
    (f) => f.flow === 'subscription_renewal_reminder' && f.anomaly === 'alert'
  );

  const cleanOrphans = async () => {
    setCleaning(true);
    setCleanResult(null);
    try {
      const res = await apiClient.request('/api/admin/magic-links/cleanup-orphans', { method: 'POST' });
      setCleanResult(`Removed ${res.deleted_tokens} phantom link(s) from ${res.orphan_user_ids?.length || 0} orphaned account(s).`);
      load();
    } catch (err) {
      setCleanResult(err?.message || 'Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-5" style={{ border: '1px solid #E0E0E0' }} data-testid="magic-link-health-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="rounded-full p-1.5" style={{ background: `${PURPLE}15` }}>
            <LinkIcon size={16} style={{ color: PURPLE }} />
          </div>
          <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Magic-Link Health</h3>
        </div>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">Last 30 days</span>
      </div>

      {!data && !error && (
        <div className="py-8 text-center"><Loader2 size={20} className="animate-spin mx-auto" style={{ color: PURPLE }} /></div>
      )}

      {error && (
        <div className="py-4 text-sm text-red-600">{error}</div>
      )}

      {data && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold border-b" style={{ borderColor: '#F3F4F6' }}>
                <th className="py-2 pr-3">Flow</th>
                <th className="py-2 px-2 text-right">Minted</th>
                <th className="py-2 px-2 text-right">Sent</th>
                <th className="py-2 px-2 text-right">Completed</th>
                <th className="py-2 px-2 text-right">Conv %</th>
                <th className="py-2 pl-2 text-right">Status</th>
              </tr>
            </thead>
            <tbody data-testid="magic-link-health-rows">
              {data.flows.map((f, i) => {
                const chip = anomalyChip(f.anomaly);
                const muted = f.anomaly === 'empty';
                return (
                  <tr key={i} className="border-b last:border-b-0" style={{ borderColor: '#F3F4F6', opacity: muted ? 0.55 : 1 }}>
                    <td className="py-2.5 pr-3 font-medium" style={{ color: '#1A1209' }}>
                      {labelFor(f.flow)}
                      {f.source === 'legacy' && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-gray-400">legacy</span>}
                    </td>
                    <td className="py-2.5 px-2 text-right tabular-nums">{f.minted}</td>
                    <td className="py-2.5 px-2 text-right tabular-nums">{f.sent}</td>
                    <td className="py-2.5 px-2 text-right tabular-nums">
                      {f.completed}
                      {(f.declined > 0 || f.expired > 0) && (
                        <span className="ml-1 text-[10px] text-gray-400">
                          {f.declined > 0 && `· ${f.declined}↓`}{f.expired > 0 && ` · ${f.expired}⊘`}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-2 text-right font-semibold tabular-nums" style={{ color: f.conversion_pct >= 50 ? '#059669' : f.conversion_pct >= 15 ? '#1A1209' : '#B45309' }}>
                      {f.conversion_pct}%
                    </td>
                    <td className="py-2.5 pl-2 text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: chip.bg, color: chip.color }}>
                        {chip.icon}{chip.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[10px] text-gray-400 mt-3">
        ↓ declined · ⊘ expired · &lt;15% conv = warn · 0 sent with ≥1 minted = likely broken email/SMS pipeline.
      </p>

      {renewalBroken && (
        <div className="mt-2 flex items-center gap-2">
          <button
            data-testid="magic-link-cleanup-orphans-btn"
            onClick={cleanOrphans}
            disabled={cleaning}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
            style={{ background: '#FEE2E2', color: '#B91C1C' }}
          >
            {cleaning ? <Loader2 size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
            Clean up phantom renewal links
          </button>
          <span className="text-[10px] text-gray-400">Removes reminder links minted for deleted/orphaned accounts.</span>
        </div>
      )}
      {cleanResult && (
        <p className="text-[11px] mt-2 font-medium" style={{ color: '#059669' }} data-testid="magic-link-cleanup-result">{cleanResult}</p>
      )}
    </div>
  );
};

export default MagicLinkHealthCard;
